import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api, { API, formatMoney, formatMoney2, formatNumber, formatPct, ABCD_ORDER, ABCD_COLORS, ABCD_LABELS } from "../lib/api";
import { ArrowLeft, Download, FileText, Loader2, Search, AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  Legend
} from "recharts";
import DrillDown from "../components/DrillDown";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";
import { ServerProductsExport, PRODUCT_COLS, col } from "../lib/exportCols";
import { periodoVentas } from "../lib/periodo";
import { useRowHover } from "../components/RowHover";

// Suggested Min/Max for Especialidades (prescription drugs) based on ABCD class.
// Returns null when the product is not in an "Especialidades" family.
const MIN_MAX_RULES = {
  AA: { min: 2, max: 10, scale: true },
  A:  { min: 2, max: 7,  scale: true },
  BB: { min: 1, max: 1,  scale: false },
  B:  { min: 1, max: 1,  scale: false },
  CC: { min: 1, max: 1,  scale: false },
  C:  { min: 0, max: 0,  scale: false },
  D:  { min: 0, max: 0,  scale: false },
};
function suggestMinMax(product, periodDays) {
  const fam = (product?.familia || "").toLowerCase();
  if (!fam.includes("especialidades")) return null;
  const rule = MIN_MAX_RULES[product.abcd];
  if (!rule) return null;
  if (!rule.scale) return { min: rule.min, max: rule.max };
  const daily = (product.uds_vendidas || 0) / (periodDays || 365);
  return {
    min: daily > 0 ? Math.ceil(daily * rule.min) : 0,
    max: daily > 0 ? Math.ceil(daily * rule.max) : 0,
  };
}

const TABS = [
  { key: "resumen", label: "Resumen" },
  { key: "ventas", label: "ABCD Ventas" },
  { key: "stock", label: "ABCD Stock" },
  { key: "laboratorios", label: "Laboratorios" },
  { key: "stockd", label: "Stock D" },
  { key: "listado", label: "Listado" },
];

function AbcdPill({ code }) {
  return <span className={`abcd-pill abcd-${code}`}>{code}</span>;
}

function Conclusion({ value }) {
  const cls = value === "PERFECTO" ? "badge-perfecto" : value === "OK" ? "badge-ok" : "badge-mejorar";
  return <span className={cls}>{value}</span>;
}

const SEGMENTS = [
  { key: "all", label: "Todo", hint: "Global" },
  { key: "farmacia", label: "Farmacia", hint: "CN ≥ 400.000" },
  { key: "parafarmacia", label: "Parafarmacia", hint: "CN < 400.000" },
];

function SegmentToggle({ value, onChange }) {
  return (
    <div className="inline-flex bg-slate-100 border border-slate-200 rounded-lg p-0.5" data-testid="segment-toggle">
      {SEGMENTS.map((s) => (
        <button
          key={s.key}
          onClick={() => onChange(s.key)}
          title={s.hint}
          data-testid={`segment-toggle-${s.key}`}
          className={
            "px-3 py-1.5 text-xs font-semibold rounded-md transition " +
            (value === s.key
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-700")
          }
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

export default function StudyDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [study, setStudy] = useState(null);
  const [segmentedData, setSegmentedData] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "resumen");
  const [segment, setSegment] = useState(searchParams.get("segment") || "all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/studies/${id}`);
        setStudy(data);
        // Auto-open drilldown if query params present
        const labParam = searchParams.get("lab");
        const famParam = searchParams.get("familia");
        if (labParam && data.by_lab_sales) {
          const labStock = (data.by_lab_stock || []).find(r => r.label === labParam) || {};
          const labSales = data.by_lab_sales.find(r => r.label === labParam) || { label: labParam, total: 0 };
          const breakdown = ABCD_ORDER.map((k) => ({ code: k, label: ABCD_LABELS[k], value: labSales[k] || 0, stock: labStock[k] || 0 }));
          setDrill({
            title: labParam,
            subtitle: `Laboratorio · Ventas: ${labSales.total ? labSales.total.toLocaleString("es-ES",{style:"currency",currency:"EUR"}) : "0€"}`,
            filters: { laboratorio: labParam },
            abcdBreakdown: breakdown,
            chartTitle: "Ventas por clasificación ABCD",
          });
        } else if (famParam && data.by_family_sales) {
          const famStock = (data.by_family_stock || []).find(r => r.label === famParam) || {};
          const famSales = data.by_family_sales.find(r => r.label === famParam) || { label: famParam, total: 0 };
          const breakdown = ABCD_ORDER.map((k) => ({ code: k, label: ABCD_LABELS[k], value: famSales[k] || 0, stock: famStock[k] || 0 }));
          setDrill({
            title: famParam,
            subtitle: `Familia`,
            filters: { familia: famParam },
            abcdBreakdown: breakdown,
            chartTitle: "Ventas por clasificación ABCD",
          });
        }
      } catch (e) {
        setError("No se pudo cargar el estudio");
      } finally { setLoading(false); }
    })();
  }, [id, searchParams]);

  // Fetch segmented aggregates on demand (cached for the session)
  useEffect(() => {
    if (!id || segmentedData) return;
    if (segment === "all") return;
    (async () => {
      try {
        const { data } = await api.get(`/studies/${id}/segmented`);
        setSegmentedData(data);
      } catch (e) { /* ignore */ }
    })();
  }, [id, segment, segmentedData]);

  // Build the "effective" study object based on selected segment
  const effectiveStudy = useMemo(() => {
    if (!study) return null;
    if (segment === "all") return study;
    if (!segmentedData) return null; // still loading segmented data
    const seg = segmentedData[segment];
    if (!seg) return study;
    return {
      ...study,
      total_products: segmentedData.counts[segment],
      totals: seg.totals,
      abcd_counts: seg.abcd_counts,
      abcd_totals: seg.abcd_totals,
      by_family_sales: seg.by_family_sales,
      by_family_stock: seg.by_family_stock,
      by_lab_sales: seg.by_lab_sales,
      by_lab_stock: seg.by_lab_stock,
      stock_d_by_year: seg.stock_d_by_year,
      stock_d_subdivision: seg.stock_d_subdivision,
      families: seg.families,
      labs: seg.labs,
    };
  }, [study, segment, segmentedData]);

  const onSegmentChange = (s) => {
    setSegment(s);
    setDrill(null); // close any open drill when segment changes
    const next = new URLSearchParams(searchParams);
    if (s === "all") next.delete("segment");
    else next.set("segment", s);
    // Also clear lab/familia query so the drill doesn't re-open on reload with stale entity
    next.delete("lab");
    next.delete("familia");
    setSearchParams(next, { replace: true });
  };

  if (loading) return <div className="p-8 flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>;
  if (error || !study) return <div className="p-8 text-red-600">{error}</div>;

  const download = (type) => {
    const url = `${API}/studies/${id}/export/${type}`;
    window.open(url, "_blank");
  };

  const openDrill = (drillConfig) => {
    // Inject current segment into drilldown filters so the products list and export are filtered.
    if (segment !== "all" && drillConfig?.filters) {
      drillConfig = { ...drillConfig, filters: { ...drillConfig.filters, segment } };
    }
    setDrill(drillConfig);
  };
  const closeDrill = () => setDrill(null);

  const currentStudy = effectiveStudy || study;
  const segmentLoading = segment !== "all" && !segmentedData;

  return (
    <div className="fade-in" data-testid="study-detail-page">
      <div className="px-8 pt-8 pb-4 border-b border-slate-200 bg-white sticky top-0 lg:top-[64px] z-20">
        <Link to="/studies" className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
          <ArrowLeft size={12} /> Volver
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">{study.name}</h1>
            <p className="text-sm text-slate-600 mt-1">
              Periodo: <span className="font-mono font-semibold">{study.period_days}d</span>
              {study.auto_period_days && study.auto_period_days !== study.period_days && (
                <span className="text-slate-400"> (auto: {study.auto_period_days}d)</span>
              )}
              {" · "}{formatNumber(currentStudy.total_products)} productos · Subido {new Date(study.created_at).toLocaleDateString("es-ES")}
            </p>
          </div>
          <div className="flex gap-2 items-start">
            <SegmentToggle value={segment} onChange={onSegmentChange} />
            <button data-testid="export-excel-button" onClick={() => download("excel")} className="btn btn-outline btn-sm">
              <Download size={14} /> Excel
            </button>
            <button data-testid="export-pdf-button" onClick={() => download("pdf")} className="btn btn-outline btn-sm">
              <FileText size={14} /> PDF
            </button>
          </div>
        </div>

        <div className="tabs-list mt-6">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={"tab-trigger " + (tab === t.key ? "active" : "")}
                    data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-8">
        {segmentLoading ? (
          <div className="flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Calculando segmento {segment}…</div>
        ) : (
          <>
            {tab === "resumen" && <ResumenTab study={currentStudy} openDrill={openDrill} />}
            {tab === "ventas" && <AbcdVentasTab study={currentStudy} openDrill={openDrill} />}
            {tab === "stock" && <AbcdStockTab study={currentStudy} openDrill={openDrill} />}
            {tab === "laboratorios" && <LabsTab study={currentStudy} openDrill={openDrill} />}
            {tab === "stockd" && <StockDTab study={currentStudy} id={id} segment={segment} openDrill={openDrill} />}
            {tab === "listado" && <ListadoTab id={id} study={currentStudy} segment={segment} />}
          </>
        )}
      </div>

      <DrillDown
        open={!!drill}
        onClose={closeDrill}
        studyId={id}
        title={drill?.title || ""}
        subtitle={drill?.subtitle}
        periodo={periodoVentas(currentStudy || study)}
        filters={drill?.filters || {}}
        abcdBreakdown={drill?.abcdBreakdown}
        familyBreakdown={drill?.familyBreakdown}
        chartTitle={drill?.chartTitle}
      />
    </div>
  );
}

function ResumenTab({ study, openDrill }) {
  const t = study.totals;
  const data = ABCD_ORDER.map((k) => ({
    code: k, label: ABCD_LABELS[k],
    count: study.abcd_counts[k] || 0,
    uds_sold: study.abcd_totals[k]?.units_sold || 0,
    stock: study.abcd_totals[k]?.stock_value || 0,
    sales: study.abcd_totals[k]?.sales_value || 0,
    pct_stock: t.total_stock_value ? (study.abcd_totals[k]?.stock_value || 0) / t.total_stock_value : 0,
    pct_sales: t.total_sales_value ? (study.abcd_totals[k]?.sales_value || 0) / t.total_sales_value : 0,
  }));
  // Default order is the natural ABCD order; clicking a header switches to sorting
  const sort = useSortable(data, null, "desc");
  const rows = sort.sortKey ? sort.sortedRows : data;
  const drillGrupo = (g) => {
    const familyBreakdown = (study.by_family_sales || []).map((f) => {
      const stockRow = (study.by_family_stock || []).find((s) => s.label === f.label) || {};
      return { familia: f.label, sales: g.codes.reduce((t, c) => t + (f[c] || 0), 0), stock: g.codes.reduce((t, c) => t + (stockRow[c] || 0), 0) };
    }).filter((f) => f.sales || f.stock);
    openDrill({
      title: `${g.sub} — ${g.label}`,
      subtitle: `Todos los productos del estudio con ABCD ${g.codes.join(", ")} · agrupados por familia`,
      filters: { abcd: g.codes.join(",") },
      familyBreakdown,
    });
  };
  const drillAbcd = (k) => {
    const familyBreakdown = (study.by_family_sales || []).map((f) => {
      const stockRow = (study.by_family_stock || []).find((s) => s.label === f.label) || {};
      return { familia: f.label, sales: f[k] || 0, stock: stockRow[k] || 0 };
    });
    openDrill({
      title: `Clasificación ${k} — ${ABCD_LABELS[k]}`,
      subtitle: `Todos los productos del estudio con ABCD = ${k} · agrupados por familia`,
      filters: { abcd: k },
      familyBreakdown,
    });
  };
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="card kpi-card">
          <div className="kpi-label">Valor stock</div>
          <div className="kpi-value">{formatMoney(t.total_stock_value)}</div>
          <div className="kpi-sub">{formatNumber(t.total_stock_units)} uds</div>
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">Total ventas</div>
          <div className="kpi-value">{formatMoney(t.total_sales_value)}</div>
          <div className="kpi-sub">{formatNumber(t.total_units)} uds vendidas</div>
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">Rotación global</div>
          <div className="kpi-value">{(t.rotacion_global || 0).toFixed(2)}×</div>
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">Productos</div>
          <div className="kpi-value">{formatNumber(t.total_products)}</div>
          <div className="kpi-sub">{study.families.length} familias · {study.labs.length} labs</div>
        </div>
      </div>

      {/* ABCD table */}
      <div className="card p-6 mb-6">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Distribución ABCD</div>
        <h3 className="font-display text-xl font-bold mt-1 mb-4">Por clasificación</h3>
        <div className="overflow-x-auto">
          <table className="dense">
            <thead>
              <tr>
                <th><SortHeader label="Código" k="code" sort={sort} defaultDir="asc" /></th>
                <th><SortHeader label="Periodo" k="label" sort={sort} defaultDir="asc" /></th>
                <th className="num"><SortHeader label="Productos" k="count" sort={sort} num /></th>
                <th className="num"><SortHeader label="Uds. Vendidas" k="uds_sold" sort={sort} num /></th>
                <th className="num"><SortHeader label="Valor Stock" k="stock" sort={sort} num /></th>
                <th className="num"><SortHeader label="Ventas" k="sales" sort={sort} num /></th>
                <th className="num"><SortHeader label="% Stock" k="pct_stock" sort={sort} num /></th>
                <th className="num"><SortHeader label="% Ventas" k="pct_sales" sort={sort} num /></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const pctStock = d.pct_stock;
                const pctSales = d.pct_sales;
                return (
                  <tr key={d.code} className="cursor-pointer" onClick={() => drillAbcd(d.code)} data-testid={`drill-abcd-${d.code}`}>
                    <td><AbcdPill code={d.code} /></td>
                    <td className="font-mono text-xs">{d.label}</td>
                    <td className="num">{formatNumber(d.count)}</td>
                    <td className="num">{formatNumber(d.uds_sold)}</td>
                    <td className="num">{formatMoney(d.stock)}</td>
                    <td className="num">{formatMoney(d.sales)}</td>
                    <td className="num">{formatPct(pctStock)}</td>
                    <td className="num">{formatPct(pctSales)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ClaseVsVentasChart data={data} onGrupo={drillGrupo} />
    </div>
  );
}

function ClaseVsVentasChart({ data, onGrupo }) {
  const totN = data.reduce((t, d) => t + (d.count || 0), 0);
  const totV = data.reduce((t, d) => t + (d.sales || 0), 0);
  const rows = data.map((d) => ({
    code: d.code,
    productos: totN ? (100 * (d.count || 0)) / totN : 0,
    ventas: totV ? Math.max(0, (100 * (d.sales || 0)) / totV) : 0,
    count: d.count, sales: d.sales,
  }));
  const pct = (v) => `${v.toLocaleString("es-ES", { maximumFractionDigits: 1 })} %`;
  return (
    <div className="card p-6" data-testid="clase-vs-ventas-chart">
      <h3 className="font-display text-lg font-bold">Productos y ventas por clase</h3>
      <p className="text-xs text-slate-500 mb-3">% de productos que tiene cada clase frente al % de las ventas que aporta.</p>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={rows} barGap={3}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="code" tick={{ fontSize: 12, fontWeight: 600 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} %`} />
          <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 6, fontSize: 12 }}
                   formatter={(v, name, item) => (name === "% de productos"
                     ? [`${pct(v)} · ${formatNumber(item.payload.count)} productos`, name]
                     : [`${pct(v)} · ${formatMoney(item.payload.sales)}`, name])} />
          <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="productos" name="% de productos" fill="#94a3b8" radius={[4, 4, 0, 0]} />
          <Bar dataKey="ventas" name="% de ventas" fill="#10b981" radius={[4, 4, 0, 0]}>
            {rows.map((d) => <Cell key={d.code} fill={ABCD_COLORS[d.code]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ResumenAltaRotacion data={data} onGrupo={onGrupo} />
    </div>
  );
}

const GRUPOS_ROT = [
  { k: "aa-bb", label: "AA–BB", sub: "Alta rotación", codes: ["AA", "A", "BB", "B"], cls: "border-emerald-500 bg-emerald-50/60", txt: "text-emerald-700" },
  { k: "c-cc", label: "C–CC", sub: "Baja rotación", codes: ["CC", "C"], cls: "border-amber-500 bg-amber-50/60", txt: "text-amber-700" },
  { k: "d", label: "D", sub: "Stock parado", codes: ["D"], cls: "border-red-500 bg-red-50/60", txt: "text-red-700" },
];

function ResumenAltaRotacion({ data, onGrupo }) {
  const sum = (k, f = () => true) => data.filter(f).reduce((t, d) => t + (d[k] || 0), 0);
  const stT = sum("stock"), vT = sum("sales");
  const p = (a, b) => `${(b ? (100 * a) / b : 0).toLocaleString("es-ES", { maximumFractionDigits: 1 })} %`;
  return (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="resumen-rotacion">
      {GRUPOS_ROT.map((g) => {
        const f = (d) => g.codes.includes(d.code);
        const st = sum("stock", f), v = sum("sales", f);
        return (
          <button type="button" key={g.k} onClick={() => onGrupo(g)} title="Ver desglose de productos" className={`text-left rounded-xl border-2 ${g.cls} px-4 py-3 cursor-pointer transition-transform hover:-translate-y-0.5 hover:shadow-md`} data-testid={`resumen-${g.k}`}>
            <div className={`text-[10px] font-extrabold uppercase tracking-widest ${g.txt}`}>{g.sub}</div>
            {[["Stock", st, p(st, stT), "del stock", "stock"], ["Ventas", v, p(v, vT), "de las ventas", "ventas"]].map(([l, eur, pc, s, k]) => (
              <div key={k} className="flex items-baseline gap-2 flex-wrap mt-1" data-testid={`resumen-${g.k}-${k}`}>
                <span className={`text-xs font-extrabold uppercase tracking-wider w-24 ${g.txt}`}>{l} {g.label}</span>
                <span className="font-display text-xl font-extrabold">{formatMoney(eur)}</span>
                <span className={`font-display text-lg font-bold ${g.txt}`}>{pc}</span>
                <span className="text-xs text-slate-500">{s}</span>
              </div>
            ))}
            <div className={`text-[11px] font-semibold mt-2 ${g.txt}`}>Ver productos →</div>
          </button>
        );
      })}
    </div>
  );
}

function AbcdVentasTab({ study, openDrill }) {
  const data = study.by_family_sales;
  const sort = useSortable(data, "total", "desc");
  const top = data.slice(0, 12).map((r) => ({
    name: r.label.length > 18 ? r.label.slice(0, 18) + "…" : r.label,
    ...ABCD_ORDER.reduce((acc, k) => ({ ...acc, [k]: r[k] || 0 }), {}),
  }));

  // Build ABCD breakdown for a family using counts via study.abcd_totals isn't enough;
  // we use the family row values themselves as sales reference + we fetch counts via API in the panel.
  const drillCell = (familia, abcd, value) => openDrill({
    title: `${familia} · ${abcd}`,
    subtitle: `${ABCD_LABELS[abcd]} — Ventas en esta intersección: ${formatMoney(value)}`,
    filters: { familia, abcd },
  });
  const drillFamily = (familia, row) => {
    // Find stock row for this family to enable internal efficiency analysis
    const stockRow = (study.by_family_stock || []).find((r) => r.label === familia) || {};
    const breakdown = ABCD_ORDER.map((k) => ({
      code: k, label: ABCD_LABELS[k], value: row[k] || 0, stock: stockRow[k] || 0,
    }));
    openDrill({
      title: familia,
      subtitle: `Familia · Total ventas: ${formatMoney(row.total)}`,
      filters: { familia },
      abcdBreakdown: breakdown,
      chartTitle: "Ventas por clasificación ABCD",
    });
  };
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Tabla pivotante</div>
      <h2 className="font-display text-2xl font-extrabold mt-1 mb-1">ABCD Ventas por Familia</h2>
      <p className="text-slate-600 mb-6">Suma de <span className="font-mono">Total Venta</span> agrupado por Familia × Clasificación. <span className="text-slate-500 italic">Haz clic en una familia o en una celda para ver el desglose de productos.</span></p>

      <div className="card p-6 mb-6">
        <h3 className="font-display text-lg font-bold mb-3">Top 12 familias por ventas (apiladas por clase)</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={top} layout="vertical" margin={{ left: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => formatMoney(v)} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
            <Tooltip formatter={(v) => formatMoney(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {ABCD_ORDER.map((k) => (
              <Bar key={k} dataKey={k} stackId="a" fill={ABCD_COLORS[k]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card overflow-x-auto">
        <div className="flex justify-end px-4 pt-3">
          <ExportButtons filename="abcd-ventas-por-familia" title="ABCD Ventas por Familia" subtitle={study.name} sheetName="Ventas familia"
                         rows={sort.sortedRows} testIdPrefix="abcd-ventas-export"
                         columns={[col.text("Familia", "label"), ...ABCD_ORDER.map((k) => col.eur(k, k)), col.eur("Total", "total")]} />
        </div>
        <table className="dense" data-testid="abcd-ventas-table">
          <thead>
            <tr>
              <th><SortHeader label="Familia" k="label" sort={sort} defaultDir="asc" /></th>
              {ABCD_ORDER.map((k) => <th key={k} className="num"><SortHeader label={k} k={k} sort={sort} num /></th>)}
              <th className="num"><SortHeader label="Total" k="total" sort={sort} num /></th>
            </tr>
          </thead>
          <tbody>
            {sort.sortedRows.map((r) => (
              <tr key={r.label}>
                <td>
                  <button
                    onClick={() => drillFamily(r.label, r)}
                    className="font-semibold text-left hover:underline hover:text-slate-900"
                    data-testid={`drill-family-${r.label}`}
                  >
                    {r.label}
                  </button>
                </td>
                {ABCD_ORDER.map((k) => (
                  <td key={k} className="num">
                    {r[k] ? (
                      <button
                        onClick={() => drillCell(r.label, k, r[k])}
                        className="hover:underline hover:text-slate-900"
                        data-testid={`drill-cell-${r.label}-${k}`}
                      >
                        {formatMoney(r[k])}
                      </button>
                    ) : "—"}
                  </td>
                ))}
                <td className="num font-bold">{formatMoney(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AbcdStockTab({ study, openDrill }) {
  const data = study.by_family_stock;
  const sort = useSortable(data, "total", "desc");
  const drillCell = (familia, abcd, value) => openDrill({
    title: `${familia} · ${abcd}`,
    subtitle: `${ABCD_LABELS[abcd]} — Valor stock en esta intersección: ${formatMoney(value)}`,
    filters: { familia, abcd },
  });
  const drillFamily = (row) => {
    const salesRow = (study.by_family_sales || []).find((r) => r.label === row.label) || {};
    const breakdown = ABCD_ORDER.map((k) => ({
      code: k, label: ABCD_LABELS[k], value: salesRow[k] || 0, stock: row[k] || 0,
    }));
    openDrill({
      title: row.label,
      subtitle: `Familia · Stock total: ${formatMoney(row.total)} · Ventas: ${formatMoney(row.ventas)} · Conclusión: ${row.conclusion}`,
      filters: { familia: row.label },
      abcdBreakdown: breakdown,
      chartTitle: "Stock vs Ventas por clasificación ABCD",
    });
  };
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Tabla pivotante con métricas</div>
      <h2 className="font-display text-2xl font-extrabold mt-1 mb-1">ABCD Stock por Familia</h2>
      <p className="text-slate-600 mb-6">Valor del stock a PVP, con métricas de rotación, % stock vs % ventas y conclusión. <span className="text-slate-500 italic">Haz clic en una familia o celda para el desglose.</span></p>

      <div className="card overflow-x-auto">
        <div className="flex justify-end px-4 pt-3">
          <ExportButtons filename="abcd-stock-por-familia" title="ABCD Stock por Familia" subtitle={study.name} sheetName="Stock familia"
                         rows={sort.sortedRows} testIdPrefix="abcd-stock-export" orientation="landscape"
                         columns={[col.text("Familia", "label"), ...ABCD_ORDER.map((k) => col.eur(k, k)), col.eur("Total stock", "total"), col.eur("Ventas", "ventas"),
                                   col.num("Rotación", "rotacion"), col.pct("% Stock", "pct_stock"), col.pct("% Ventas", "pct_ventas"), col.text("Conclusión", "conclusion")]} />
        </div>
        <table className="dense" data-testid="abcd-stock-table">
          <thead>
            <tr>
              <th><SortHeader label="Familia" k="label" sort={sort} defaultDir="asc" /></th>
              {ABCD_ORDER.map((k) => <th key={k} className="num"><SortHeader label={k} k={k} sort={sort} num /></th>)}
              <th className="num"><SortHeader label="Total stock" k="total" sort={sort} num /></th>
              <th className="num"><SortHeader label="Ventas" k="ventas" sort={sort} num /></th>
              <th className="num"><SortHeader label="Rotación" k="rotacion" sort={sort} num /></th>
              <th className="num"><SortHeader label="% Stock" k="pct_stock" sort={sort} num /></th>
              <th className="num"><SortHeader label="% Ventas" k="pct_ventas" sort={sort} num /></th>
              <th><SortHeader label="Conclusión" k="conclusion" sort={sort} defaultDir="asc" /></th>
            </tr>
          </thead>
          <tbody>
            {sort.sortedRows.map((r) => (
              <tr key={r.label}>
                <td>
                  <button onClick={() => drillFamily(r)} className="font-semibold text-left hover:underline" data-testid={`drill-stock-family-${r.label}`}>
                    {r.label}
                  </button>
                </td>
                {ABCD_ORDER.map((k) => (
                  <td key={k} className="num">
                    {r[k] ? (
                      <button onClick={() => drillCell(r.label, k, r[k])} className="hover:underline" data-testid={`drill-stock-cell-${r.label}-${k}`}>
                        {formatMoney(r[k])}
                      </button>
                    ) : "—"}
                  </td>
                ))}
                <td className="num font-bold">{formatMoney(r.total)}</td>
                <td className="num">{formatMoney(r.ventas)}</td>
                <td className="num">{(r.rotacion || 0).toFixed(2)}×</td>
                <td className="num">{formatPct(r.pct_stock)}</td>
                <td className="num">{formatPct(r.pct_ventas)}</td>
                <td><Conclusion value={r.conclusion} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ratioVS = (v, st) => (st > 0 ? v / st : null);
const fmtRatio = (r) => (r == null ? "—" : `${r.toFixed(2).replace(".", ",")}×`);

function LabCell({ v, st, onClick, testid }) {
  if (!v && !st) return <span className="text-slate-300">—</span>;
  const r = ratioVS(v, st);
  return (
    <button onClick={onClick} className="text-right leading-tight hover:underline w-full" data-testid={testid}>
      <div className="font-semibold">{v ? formatMoney(v) : "—"}</div>
      <div className="text-[10px] text-slate-500">st {st ? formatMoney(st) : "—"}</div>
      <div className={"text-[10px] font-bold " + (r == null ? "text-slate-300" : r >= 1 ? "text-emerald-700" : "text-red-600")}>{fmtRatio(r)}</div>
    </button>
  );
}

function LabsTab({ study, openDrill }) {
  const top = study.by_lab_sales.slice(0, 15);
  const rows = useMemo(() => {
    const stockMap = Object.fromEntries((study.by_lab_stock || []).map((r) => [r.label, r]));
    const salesMap = Object.fromEntries((study.by_lab_sales || []).map((r) => [r.label, r]));
    return [...new Set([...Object.keys(salesMap), ...Object.keys(stockMap)])].map((label) => {
      const v = salesMap[label] || {}, st = stockMap[label] || {};
      const row = { label, total: v.total || 0, stock_total: st.total || 0, ratio_total: ratioVS(v.total || 0, st.total || 0) };
      ABCD_ORDER.forEach((k) => { row[k] = v[k] || 0; row[`st_${k}`] = st[k] || 0; row[`r_${k}`] = ratioVS(v[k] || 0, st[k] || 0); });
      return row;
    });
  }, [study]);
  const [q, setQ] = useState("");
  const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const visibles = useMemo(() => (q.trim() ? rows.filter((r) => norm(r.label).includes(norm(q.trim()))) : rows), [rows, q]);
  const sort = useSortable(visibles, "total", "desc");

  const drillLab = (row) => {
    const breakdown = ABCD_ORDER.map((k) => ({ code: k, label: ABCD_LABELS[k], value: row[k] || 0, stock: row[`st_${k}`] || 0 }));
    openDrill({
      title: row.label,
      subtitle: `Laboratorio · Ventas: ${formatMoney(row.total)} · Stock: ${formatMoney(row.stock_total)} · V/S ${fmtRatio(row.ratio_total)}`,
      filters: { laboratorio: row.label },
      abcdBreakdown: breakdown,
      chartTitle: "Ventas y stock por clasificación ABCD",
    });
  };
  const drillLabCell = (row, abcd) => openDrill({
    title: `${row.label} · ${abcd}`,
    subtitle: `${ABCD_LABELS[abcd]} — Ventas: ${formatMoney(row[abcd])} · Stock: ${formatMoney(row[`st_${abcd}`])} · V/S ${fmtRatio(row[`r_${abcd}`])}`,
    filters: { laboratorio: row.label, abcd },
  });
  const expCols = [col.text("Laboratorio", "label"),
    ...ABCD_ORDER.flatMap((k) => [col.eur(`${k} ventas`, k), col.eur(`${k} stock`, `st_${k}`), col.num(`${k} V/S`, `r_${k}`)]),
    col.eur("Total ventas", "total"), col.eur("Total stock", "stock_total"), col.num("V/S total", "ratio_total")];
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Por laboratorio</div>
      <h2 className="font-display text-2xl font-extrabold mt-1 mb-1">Análisis por Laboratorio</h2>
      <p className="text-slate-600 mb-6">{study.labs.length} laboratorios detectados. En cada clase verás la <b>venta</b>, el <b>stock</b> (st) y el <b>ratio venta/stock</b> (verde si vende más de lo que tiene). <span className="text-slate-500 italic">Haz clic en un laboratorio o en una celda para ver los productos.</span></p>

      <div className="card p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="labs-search-box">
        <Search size={18} className="text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar laboratorio… (ej. ISDIN, Uriach)"
               className="input flex-1 min-w-[200px]" data-testid="labs-search-input"
               onKeyDown={(e) => { if (e.key === "Enter" && sort.sortedRows.length === 1) drillLab(sort.sortedRows[0]); }} />
        {q && (
          <>
            <span className="text-sm text-slate-600" data-testid="labs-search-count">{visibles.length} de {rows.length}</span>
            <button onClick={() => setQ("")} className="btn btn-outline btn-sm" data-testid="labs-search-clear">Limpiar</button>
          </>
        )}
      </div>

      {!q && (
      <div className="card p-6 mb-6">
        <h3 className="font-display text-lg font-bold mb-3">Top 15 laboratorios por ventas</h3>
        <ResponsiveContainer width="100%" height={520}>
          <BarChart data={top.map(r => ({ name: r.label.length > 22 ? r.label.slice(0, 22) + "…" : r.label, ventas: r.total, stock: r.stock_total || 0 }))} layout="vertical" margin={{ left: 30 }} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => formatMoney(v)} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={160} />
            <Tooltip formatter={(v, n) => [formatMoney(v), n]} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="ventas" name="Ventas" fill="#0F172A" radius={[0, 4, 4, 0]} />
            <Bar dataKey="stock" name="Stock (PVP)" fill="#dc2626" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      )}

      <div className="card overflow-x-auto">
        <div className="flex justify-end px-4 pt-3">
          <ExportButtons filename="laboratorios" title="Análisis por Laboratorio (ventas, stock y ratio por clase)" subtitle={study.name} sheetName="Laboratorios"
                         rows={sort.sortedRows} testIdPrefix="labs-export" orientation="landscape" columns={expCols} />
        </div>
        <table className="dense compact" data-testid="labs-table">
          <thead>
            <tr>
              <th><SortHeader label="Laboratorio" k="label" sort={sort} defaultDir="asc" /></th>
              {ABCD_ORDER.map((k) => <th key={k} className="num"><SortHeader label={k} k={k} sort={sort} num /></th>)}
              <th className="num"><SortHeader label="Ventas" k="total" sort={sort} num /></th>
              <th className="num"><SortHeader label="Stock" k="stock_total" sort={sort} num /></th>
              <th className="num"><SortHeader label="V/S" k="ratio_total" sort={sort} num /></th>
            </tr>
          </thead>
          <tbody>
            {sort.sortedRows.map((r) => (
              <tr key={r.label} data-testid={`lab-row-${r.label}`}>
                <td className="desc">
                  <button onClick={() => drillLab(r)} className="font-semibold text-left hover:underline" data-testid={`drill-lab-${r.label}`}>
                    {r.label}
                  </button>
                </td>
                {ABCD_ORDER.map((k) => (
                  <td key={k} className="num">
                    <LabCell v={r[k]} st={r[`st_${k}`]} onClick={() => drillLabCell(r, k)} testid={`drill-lab-cell-${r.label}-${k}`} />
                  </td>
                ))}
                <td className="num font-bold">{formatMoney(r.total)}</td>
                <td className="num">{formatMoney(r.stock_total)}</td>
                <td className={"num font-bold " + (r.ratio_total == null ? "text-slate-300" : r.ratio_total >= 1 ? "text-emerald-700" : "text-red-600")}>{fmtRatio(r.ratio_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function mergeYears(raw) {
  const by = {};
  (raw || []).forEach((y) => {
    const ok = y.year != null && y.year !== "Sin fecha" && !Number.isNaN(Number(y.year));
    const k = ok ? String(y.year) : "sin";
    by[k] = by[k] || { year: ok ? Number(y.year) : null, key: k, count: 0, stock_value: 0, stock_units: 0 };
    by[k].count += y.count || 0;
    by[k].stock_value += y.stock_value || 0;
    by[k].stock_units += y.stock_units || 0;
  });
  return Object.values(by).filter((y) => y.count > 0).sort((a, b) => (a.year ?? 9999) - (b.year ?? 9999));
}

function StockDFamilias({ id, year, segment, familia, onFamilia, label }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const params = { d_year: year };
    if (segment && segment !== "all") params.segment = segment;
    setRows(null);
    api.get(`/studies/${id}/stock-d/familias`, { params }).then((r) => setRows(r.data.familias)).catch(() => setRows([]));
  }, [id, year, segment]);
  if (!rows) return <div className="card p-6 mb-6 text-sm text-slate-500"><Loader2 size={14} className="inline animate-spin mr-2" />Cargando familias…</div>;
  const total = rows.reduce((t, r) => t + r.valor_pvp, 0);
  return (
    <div className="card p-6 mb-6" data-testid="stock-d-familias">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h3 className="font-display text-lg font-bold">Stock D con última entrada en {label} · por familia</h3>
        <ExportButtons filename={`stock-d-${label}-familias`} title={`Stock D con última entrada en ${label} · por familia`} sheetName="Familias D"
                       rows={rows.map((r) => ({ ...r, pct: total ? r.valor_pvp / total : 0 }))} testIdPrefix="stock-d-familias-export"
                       columns={[col.text("Familia", "familia"), col.num("Productos", "count"), col.num("Uds", "stock"), col.eur("Valor (PVP)", "valor_pvp"),
                                 col.eur("Valor (coste)", "valor_pmc"), col.pct("% del año", "pct")]} />
      </div>
      <p className="text-xs text-slate-500 mb-3">Pulsa una familia para abrir su desglose con todos sus productos.</p>
      <div className="overflow-x-auto">
        <table className="dense">
          <thead><tr><th>Familia</th><th className="num">Productos</th><th className="num">Uds</th><th className="num">Valor (PVP)</th><th className="num">Valor (coste)</th><th className="num">% del año</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.familia} onClick={() => onFamilia(r.familia)} data-testid={`stock-d-familia-${r.familia}`}
                  className={"cursor-pointer " + (familia === r.familia ? "bg-red-50" : "")}>
                <td className="font-semibold">{r.familia}</td>
                <td className="num">{formatNumber(r.count)}</td>
                <td className="num">{formatNumber(r.stock)}</td>
                <td className="num">{formatMoney(r.valor_pvp)}</td>
                <td className="num">{formatMoney(r.valor_pmc)}</td>
                <td className="num">{formatPct(total ? r.valor_pvp / total : 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StockDTab({ study, id, segment, openDrill }) {
  const sub = study.stock_d_subdivision;
  const years = mergeYears(study.stock_d_by_year);
  const [filter, setFilter] = useState("ALL"); // ALL | D_MUERTO | D_RECIENTE
  const [year, setYear] = useState("");
  const [familia, setFamilia] = useState("");
  const [laboratorio, setLaboratorio] = useState("");
  const [canal, setCanal] = useState("");
  const seg = canal || segment;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const sort = useSortable(products, "valor_pvp", "desc");
  const hov = useRowHover();

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = { study_id: id, abcd: "D", limit: 100 };
      if (filter !== "ALL") params.d_type = filter;
      if (year) params.d_year = year;
      if (familia) params.familia = familia;
      if (laboratorio) params.laboratorio = laboratorio;
      if (seg && seg !== "all") params.segment = seg;
      try {
        const { data } = await api.get(`/studies/${id}/products`, { params });
        setProducts(data.items);
      } finally { setLoading(false); }
    })();
  }, [id, filter, year, familia, laboratorio, seg]);
  const pickYear = (k) => { setYear(year === k ? "" : k); setFamilia(""); };
  const yearLabel = (k) => (k === "sin" ? "fecha desconocida" : k);

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Productos sin ventas</div>
      <h2 className="font-display text-2xl font-extrabold mt-1 mb-1">Stock D — Sin movimiento</h2>
      <p className="text-slate-600 mb-6">Identifica productos sin ventas en el periodo. Subdividido por antigüedad desde la última entrada.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card kpi-card border-l-4 border-red-500" data-testid="d-muerto-card">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-red-500" />
            <div className="kpi-label">D MUERTO (&gt;1 año)</div>
          </div>
          <div className="kpi-value text-red-600">{formatMoney(sub.d_muerto.stock_value)}</div>
          <div className="kpi-sub">{sub.d_muerto.count} productos · {formatNumber(sub.d_muerto.stock_units)} uds inmovilizadas</div>
        </div>
        <div className="card kpi-card border-l-4 border-amber-500" data-testid="d-reciente-card">
          <div className="kpi-label">D RECIENTE (&lt;1 año)</div>
          <div className="kpi-value text-amber-600">{formatMoney(sub.d_reciente.stock_value)}</div>
          <div className="kpi-sub">{sub.d_reciente.count} productos · {formatNumber(sub.d_reciente.stock_units)} uds</div>
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">Total D</div>
          <div className="kpi-value">{formatMoney(sub.d_muerto.stock_value + sub.d_reciente.stock_value)}</div>
          <div className="kpi-sub">{sub.d_muerto.count + sub.d_reciente.count} productos</div>
        </div>
      </div>

      {years.length > 0 && (
        <div className="card p-6 mb-6">
          <h3 className="font-display text-lg font-bold">Stock D por año de última entrada</h3>
          <p className="text-xs text-slate-500 mb-3">Pulsa una barra para ver ese stock parado por familia.</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={years.map((y) => ({ key: y.key, year: y.key === "sin" ? "Sin fecha" : y.key, valor: y.stock_value, count: y.count }))} data-testid="stock-d-years-chart">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} interval={0} />
              <YAxis tick={{ fontSize: 11 }} width={80} domain={[0, "auto"]} allowDataOverflow tickFormatter={(v) => formatMoney(v)} />
              <Tooltip cursor={{ fill: "#fef2f2" }} contentStyle={{ borderRadius: 6, fontSize: 12 }}
                       formatter={(v, n, item) => [`${formatMoney(v)} · ${formatNumber(item.payload.count)} productos`, "Valor stock (PVP)"]} />
              <Bar dataKey="valor" name="Valor stock (PVP)" radius={[4, 4, 0, 0]} style={{ cursor: "pointer" }}
                   onClick={(d) => d?.key && pickYear(d.key)}>
                {years.map((y) => <Cell key={y.key} fill={!year || year === y.key ? "#EF4444" : "#fecaca"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {year && <StockDFamilias id={id} year={year} segment={segment} familia={familia} label={yearLabel(year)}
                                onFamilia={(f) => f && openDrill({ title: `${f} · Stock D`, subtitle: `Stock D con última entrada en ${yearLabel(year)}`,
                                  filters: { abcd: "D", familia: f, d_year: year, ...(segment && segment !== "all" ? { segment } : {}) } })} />}

      <div className="card p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <h3 className="font-display text-lg font-bold">Productos D (top 100){familia && <button onClick={() => setFamilia("")} className="ml-2 align-middle rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700" data-testid="stock-d-familia-chip">{familia} ×</button>}</h3>
          <div className="flex gap-2 items-center flex-wrap">
            <select value={canal} onChange={(e) => setCanal(e.target.value)} className="input" style={{ width: 150 }} data-testid="d-canal-filter">
              <option value="">{segment && segment !== "all" ? (segment === "farmacia" ? "Farmacia" : "Parafarmacia") : "Farmacia y para"}</option>
              <option value="all">Farmacia y para</option>
              <option value="farmacia">Farmacia</option>
              <option value="parafarmacia">Parafarmacia</option>
            </select>
            <select value={familia} onChange={(e) => setFamilia(e.target.value)} className="input" style={{ width: 180 }} data-testid="d-familia-filter">
              <option value="">Todas las familias</option>
              {(study.families || []).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <select value={laboratorio} onChange={(e) => setLaboratorio(e.target.value)} className="input" style={{ width: 190 }} data-testid="d-laboratorio-filter">
              <option value="">Todos los laboratorios</option>
              {(study.labs || []).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input" style={{ width: 180 }} data-testid="d-type-filter">
              <option value="ALL">Todos</option>
              <option value="D_MUERTO">D MUERTO (&gt;1 año)</option>
              <option value="D_RECIENTE">D RECIENTE (&lt;1 año)</option>
            </select>
            <select value={year} onChange={(e) => setYear(e.target.value)} className="input" style={{ width: 130 }} data-testid="d-year-filter">
              <option value="">Cualquier año</option>
              {years.map((y) => <option key={y.key} value={y.key}>{y.key === "sin" ? "Sin fecha" : y.key}</option>)}
            </select>
            <ServerProductsExport studyId={id} sortBy="valor_pvp" filename="stock-d-productos" title="Stock D — productos sin ventas" testIdPrefix="stockd-export"
                                  subtitle={[familia, laboratorio, filter !== "ALL" && filter, year && `Año ${year}`].filter(Boolean).join(" · ")}
                                  filters={{ abcd: "D", d_type: filter !== "ALL" ? filter : "", d_year: year, familia, laboratorio, segment: seg }}
                                  columns={[...PRODUCT_COLS.filter((c) => !["tot_venta", "uds_vendidas", "abcd"].includes(c.key)), col.text("Tipo", "d_type")]} />
          </div>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-slate-500 text-sm">Cargando…</div>
          ) : (
            <>{hov.tip}<table className="dense" data-testid="stockd-products-table">
              <thead>
                <tr>
                  <th><SortHeader label="CN" k="cn" sort={sort} defaultDir="asc" /></th>
                  <th><SortHeader label="Descripción" k="descripcion" sort={sort} defaultDir="asc" /></th>
                  <th><SortHeader label="Familia" k="familia" sort={sort} defaultDir="asc" /></th>
                  <th><SortHeader label="Laboratorio" k="laboratorio" sort={sort} defaultDir="asc" /></th>
                  <th className="num"><SortHeader label="Stock" k="stock" sort={sort} num /></th>
                  <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={sort} num /></th>
                  <th><SortHeader label="U. Entrada" k="ultima_entrada" sort={sort} defaultDir="desc" /></th>
                  <th><SortHeader label="Tipo" k="d_type" sort={sort} defaultDir="asc" /></th>
                </tr>
              </thead>
              <tbody>
                {sort.sortedRows.map((p, i) => (
                  <tr key={`d-${i}-${p.cn}`} className="cursor-help" data-testid={`stockd-row-${p.cn}`} {...hov.bind(p)}>
                    <td className="font-mono">{p.cn}</td>
                    <td>{p.descripcion}</td>
                    <td className="text-xs">{p.familia}</td>
                    <td className="text-xs">{p.laboratorio}</td>
                    <td className="num">{formatNumber(p.stock)}</td>
                    <td className="num">{formatMoney2(p.valor_pvp)}</td>
                    <td className="font-mono text-xs">{p.u_entrada ? new Date(p.u_entrada).toLocaleDateString("es-ES") : "—"}</td>
                    <td>
                      <span className={p.d_type === "D_MUERTO" ? "badge-mejorar" : "badge-ok"}>
                        {p.d_type === "D_MUERTO" ? "MUERTO" : "RECIENTE"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></>
          )}
        </div>
      </div>
    </div>
  );
}

function ListadoTab({ id, study, segment }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ search: "", abcd: "", familia: "", laboratorio: "" });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("valor_pvp");
  const [sortDir, setSortDir] = useState("desc");
  const LIMIT = 50;

  const requestSort = (k) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
    setPage(0);
  };
  const sort = { sortKey, sortDir, requestSort };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = { skip: page * LIMIT, limit: LIMIT, sort_by: sortKey, sort_dir: sortDir };
      if (filters.search) params.search = filters.search;
      if (filters.abcd) params.abcd = filters.abcd;
      if (filters.familia) params.familia = filters.familia;
      if (filters.laboratorio) params.laboratorio = filters.laboratorio;
      if (segment && segment !== "all") params.segment = segment;
      try {
        const { data: d } = await api.get(`/studies/${id}/products`, { params });
        setData(d);
      } finally { setLoading(false); }
    })();
  }, [id, page, filters, segment, sortKey, sortDir]);

  const totalPages = Math.ceil(data.total / LIMIT);

  const onFilter = (k, v) => { setFilters({ ...filters, [k]: v }); setPage(0); };

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Listado completo</div>
      <h2 className="font-display text-2xl font-extrabold mt-1 mb-1">Productos del estudio</h2>
      <p className="text-slate-600 mb-6">{formatNumber(data.total)} resultados</p>

      <div className="flex gap-3 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="listado-search" placeholder="Buscar por CN o descripción..." value={filters.search}
                 onChange={(e) => onFilter("search", e.target.value)} className="input" style={{ paddingLeft: 32 }} />
        </div>
        <select className="input" style={{ width: 130 }} value={filters.abcd} onChange={(e) => onFilter("abcd", e.target.value)} data-testid="listado-filter-abcd">
          <option value="">ABCD: todos</option>
          {ABCD_ORDER.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <select className="input" style={{ width: 200 }} value={filters.familia} onChange={(e) => onFilter("familia", e.target.value)} data-testid="listado-filter-familia">
          <option value="">Familia: todas</option>
          {study.families.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select className="input" style={{ width: 220 }} value={filters.laboratorio} onChange={(e) => onFilter("laboratorio", e.target.value)} data-testid="listado-filter-lab">
          <option value="">Lab: todos</option>
          {study.labs.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <ServerProductsExport studyId={id} sortBy={sortKey} sortDir={sortDir} filename="productos-estudio" title="Productos del estudio" testIdPrefix="listado-export"
                              subtitle={study.name} filters={{ ...filters, segment }} />
      </div>

      <div className="card overflow-x-auto">
        <table className="dense" data-testid="listado-table">
          <thead>
            <tr>
              <th><SortHeader label="CN" k="cn" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="Descripción" k="descripcion" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="Familia" k="familia" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="Laboratorio" k="laboratorio" sort={sort} defaultDir="asc" /></th>
              <th className="num"><SortHeader label="Stock" k="stock" sort={sort} num /></th>
              <th className="num"><SortHeader label="PVP" k="pvp" sort={sort} num /></th>
              <th className="num"><SortHeader label="Uds. Vendidas" k="uds_vendidas" sort={sort} num /></th>
              <th className="num"><SortHeader label="Rotación" k="rotacion" sort={sort} num /></th>
              <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={sort} num /></th>
              <th className="num">Min act.</th>
              <th className="num text-blue-700">Min sug.</th>
              <th className="num">Max act.</th>
              <th className="num text-blue-700">Max sug.</th>
              <th><SortHeader label="Última compra" k="u_entrada" sort={sort} defaultDir="desc" /></th>
              <th><SortHeader label="ABCD" k="abcd" sort={sort} defaultDir="asc" /></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="15" className="text-center text-slate-500 py-6"><Loader2 className="animate-spin inline" size={14} /> Cargando…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan="15" className="text-center text-slate-500 py-6">Sin resultados</td></tr>
            ) : (
              data.items.map((p, i) => {
                const sugg = suggestMinMax(p, study.period_days);
                return (
                <tr key={`p-${i}-${p.cn}`}>
                  <td className="font-mono">{p.cn}</td>
                  <td>{p.descripcion}</td>
                  <td className="text-xs">{p.familia}</td>
                  <td className="text-xs">{p.laboratorio}</td>
                  <td className="num">{formatNumber(p.stock)}</td>
                  <td className="num">{formatMoney2(p.pvp)}</td>
                  <td className="num">{formatNumber(p.uds_vendidas)}</td>
                  <td className="num">{(p.rotacion || 0).toFixed(2)}</td>
                  <td className="num">{formatMoney2(p.valor_pvp)}</td>
                  <td className="num">{p.s_min == null ? <span className="text-slate-300">—</span> : formatNumber(p.s_min)}</td>
                  <td className={"num font-bold " + (sugg ? "text-blue-700" : "text-slate-300")}>{sugg ? sugg.min : "—"}</td>
                  <td className="num">{p.s_max == null ? <span className="text-slate-300">—</span> : formatNumber(p.s_max)}</td>
                  <td className={"num font-bold " + (sugg ? "text-blue-700" : "text-slate-300")}>{sugg ? sugg.max : "—"}</td>
                  <td className="text-xs font-mono whitespace-nowrap">{p.u_entrada ? new Date(p.u_entrada).toLocaleDateString("es-ES") : "—"}</td>
                  <td><AbcdPill code={p.abcd} /></td>
                </tr>
              );})
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <div className="text-slate-500">Página {page + 1} de {totalPages}</div>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage(page - 1)} className="btn btn-outline btn-sm" data-testid="prev-page">← Anterior</button>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} className="btn btn-outline btn-sm" data-testid="next-page">Siguiente →</button>
          </div>
        </div>
      )}
    </div>
  );
}
